import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import clsx from 'clsx';

import type { CalendarEvent, DayPulse } from '../types';
import { eventEndDayInclusive, eventStartDay } from '../event-day';
import { type EventMovePatch, moveByDays } from '../event-drag';
import { retintEventBackground, retintEventForeground, useResolvedTheme } from '../theme';
import { useDragGesture } from '../useDragGesture';
import { useNow } from '../useNow';
import DailyNotePin from './DailyNotePin';
import DayWeather from './DayWeather';
import WeekMeteogram, { type TempScale } from './WeekMeteogram';
import type { WeatherView } from '../weather';

const BAR_HEIGHT = 18;
const BAR_GAP = 2;
const HEADER_HEIGHT = 30;
const TOP_PAD = 12;
// Approximate pixel cost of a chip row (text + leading + 2px gap below).
const CHIP_ROW_HEIGHT = 20;
// Pixel cost of the per-cell header (date number, "+" button) before chips
// start. Used to back-calculate how many chips actually fit in a cell.
const CELL_HEADER_HEIGHT = 24;
// Vertical space reserved at the bottom of a cell for the footer row (daily-note
// pin on the left, weather on the right), so chips don't render under it. Only
// reserved on days that show either.
const NOTE_FOOTER_HEIGHT = 20;

interface Props {
  anchor: Date;
  events: CalendarEvent[];
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  dayPulse?: DayPulse | null;
  onPickEvent?: (id: string, rect: DOMRect) => void;
  onCreateAt?: (d: Date, rect: DOMRect) => void;
  onEventContextMenu?: (id: string, x: number, y: number) => void;
  canMoveEvent?: (e: CalendarEvent) => boolean;
  onMoveEvent?: (e: CalendarEvent, patch: EventMovePatch) => void;
  noteDates?: Set<string>;
  todayKey?: string | null;
  onOpenDailyNote?: (date: string, rect: DOMRect) => void;
  weather?: WeatherView | null;
  onOpenWeather?: (date: string, rect: DOMRect) => void;
}

type DayRange = { start: Date; end: Date };

function eventDayRange(e: CalendarEvent): DayRange | null {
  const start = eventStartDay(e);
  if (!start) return null;
  const end = eventEndDayInclusive(e) ?? start;
  return { start, end: end < start ? start : end };
}

function isBarEvent(e: CalendarEvent, range: DayRange): boolean {
  return e.all_day || !isSameDay(range.start, range.end);
}

type Segment = {
  event: CalendarEvent;
  startCol: number;
  endCol: number;
  startsBefore: boolean;
  endsAfter: boolean;
  row: number;
};

function buildWeekSegments(
  week: Date[],
  bars: { event: CalendarEvent; range: DayRange }[]
): Segment[] {
  const weekStart = week[0];
  const weekEnd = week[6];
  type Pending = Omit<Segment, 'row'>;
  const candidates: Pending[] = [];
  for (const b of bars) {
    if (b.range.end < weekStart || b.range.start > weekEnd) continue;
    const startCol = Math.max(0, differenceInCalendarDays(b.range.start, weekStart));
    const endCol = Math.min(6, differenceInCalendarDays(b.range.end, weekStart));
    candidates.push({
      event: b.event,
      startCol,
      endCol,
      startsBefore: b.range.start < weekStart,
      endsAfter: b.range.end > weekEnd
    });
  }
  candidates.sort((a, b) => {
    if (a.startCol !== b.startCol) return a.startCol - b.startCol;
    return b.endCol - b.startCol - (a.endCol - a.startCol);
  });
  const occupied: boolean[][] = [];
  const out: Segment[] = [];
  for (const c of candidates) {
    let row = 0;
    while (true) {
      if (!occupied[row]) occupied[row] = new Array(7).fill(false);
      let fits = true;
      for (let i = c.startCol; i <= c.endCol; i++) {
        if (occupied[row][i]) {
          fits = false;
          break;
        }
      }
      if (fits) {
        for (let i = c.startCol; i <= c.endCol; i++) occupied[row][i] = true;
        out.push({ ...c, row });
        break;
      }
      row++;
    }
  }
  return out;
}

export default function MonthView({
  anchor,
  events,
  weekStartsOn = 0,
  dayPulse = null,
  onPickEvent,
  onCreateAt,
  onEventContextMenu,
  canMoveEvent,
  onMoveEvent,
  noteDates,
  todayKey,
  onOpenDailyNote,
  weather = null,
  onOpenWeather
}: Props) {
  const theme = useResolvedTheme();
  const today = useNow();

  const weeks = useMemo(() => {
    const ds = eachDayOfInterval({
      start: startOfWeek(startOfMonth(anchor), { weekStartsOn }),
      end: endOfWeek(endOfMonth(anchor), { weekStartsOn })
    });
    const out: Date[][] = [];
    for (let i = 0; i < ds.length; i += 7) out.push(ds.slice(i, i + 7));
    return out;
  }, [anchor, weekStartsOn]);

  const dowLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        format(addDays(startOfWeek(new Date(), { weekStartsOn }), i), 'EEE')
      ),
    [weekStartsOn]
  );

  const { bars, chipsByDay } = useMemo(() => {
    const b: { event: CalendarEvent; range: DayRange }[] = [];
    const chips = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const r = eventDayRange(e);
      if (!r) continue;
      if (isBarEvent(e, r)) {
        b.push({ event: e, range: r });
      } else {
        const k = format(r.start, 'yyyy-MM-dd');
        const list = chips.get(k) ?? [];
        list.push(e);
        chips.set(k, list);
      }
    }
    for (const list of chips.values()) {
      list.sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''));
    }
    return { bars: b, chipsByDay: chips };
  }, [events]);

  const showTempGraph = !!weather?.prefs.overlays.includes('month_temp');
  const showPrecipBars = !!weather?.prefs.overlays.includes('month_precip');
  const showSkyRibbon = !!weather?.prefs.overlays.includes('month_sky');
  // One temperature scale for the whole grid, so a line's height means the
  // same thing in every week row.
  const tempScale = useMemo<TempScale | null>(() => {
    if (!weather || !showTempGraph) return null;
    let min = Infinity;
    let max = -Infinity;
    for (const week of weeks) {
      for (const day of week) {
        for (const h of weather.hoursByDay.get(format(day, 'yyyy-MM-dd')) ?? []) {
          if (h.temperature_2m == null) continue;
          min = Math.min(min, h.temperature_2m);
          max = Math.max(max, h.temperature_2m);
        }
      }
    }
    return min <= max ? { min, max } : null;
  }, [weather, weeks, showTempGraph]);

  const segmentsByWeek = useMemo(
    () => weeks.map((week) => buildWeekSegments(week, bars)),
    [weeks, bars]
  );

  const cellRefs = useRef(new Map<string, HTMLDivElement>());

  // Measure a real day cell so bar/chip capacity is back-calculated from the
  // height cells actually render at (grid height / weeks undercounts when
  // min-height forces the rows taller than their 1fr share). Falls back to a
  // sane default before the first measurement lands.
  const gridRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState(160);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    function measure() {
      const cell: HTMLDivElement | undefined = cellRefs.current.values().next().value;
      const h = cell ? cell.getBoundingClientRect().height : el!.clientHeight / weeks.length;
      setRowHeight(Math.max(100, h));
    }
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [weeks.length]);

  // How many bar rows fit in a cell before spilling into "+N more", keeping
  // room for at least one chip row (which is where the overflow line renders).
  const maxBarRows = Math.max(
    1,
    Math.floor((rowHeight - HEADER_HEIGHT - CHIP_ROW_HEIGHT - 4) / (BAR_HEIGHT + BAR_GAP))
  );
  const { startDrag, consumeDragClick } = useDragGesture();
  type Drag = {
    event: CalendarEvent;
    targetDayKey: string | null;
    daysDelta: number;
  };
  const [drag, setDrag] = useState<Drag | null>(null);

  // "+N more" hover overlay: lists every event on the hovered day. Closed on
  // a short delay so the pointer can travel from the label into the card.
  type MoreHover = { dayKey: string; day: Date; rect: DOMRect };
  const [moreHover, setMoreHover] = useState<MoreHover | null>(null);
  const moreCloseTimer = useRef<number | null>(null);
  function cancelMoreClose() {
    if (moreCloseTimer.current !== null) {
      window.clearTimeout(moreCloseTimer.current);
      moreCloseTimer.current = null;
    }
  }
  function openMore(dayKey: string, day: Date, el: HTMLElement) {
    if (drag) return;
    cancelMoreClose();
    setMoreHover({ dayKey, day, rect: el.getBoundingClientRect() });
  }
  function scheduleMoreClose() {
    cancelMoreClose();
    moreCloseTimer.current = window.setTimeout(() => setMoreHover(null), 150);
  }
  useEffect(() => {
    setMoreHover(null);
  }, [weeks]);
  useEffect(() => cancelMoreClose, []);

  const moreEvents = useMemo(() => {
    if (!moreHover) return [];
    const day = moreHover.day;
    const through = bars
      .filter((b) => b.range.start <= day && day <= b.range.end)
      .map((b) => b.event);
    return [...through, ...(chipsByDay.get(moreHover.dayKey) ?? [])];
  }, [moreHover, bars, chipsByDay]);

  function dayAt(x: number, y: number): { key: string; date: Date } | null {
    for (const [key, el] of cellRefs.current) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x < r.right && y >= r.top && y < r.bottom) {
        const [yy, mm, dd] = key.split('-').map(Number);
        return { key, date: new Date(yy, mm - 1, dd) };
      }
    }
    return null;
  }

  function beginDrag(event: CalendarEvent, ev: React.MouseEvent) {
    if (!onMoveEvent || !canMoveEvent?.(event)) return;
    const origin = eventStartDay(event);
    if (!origin) return;
    startDrag(ev, {
      onMove: (mev) => {
        const hit = dayAt(mev.clientX, mev.clientY);
        if (!hit) {
          setDrag({ event, targetDayKey: null, daysDelta: 0 });
          return;
        }
        setDrag({
          event,
          targetDayKey: hit.key,
          daysDelta: differenceInCalendarDays(hit.date, origin)
        });
      },
      onEnd: (mev) => {
        const hit = dayAt(mev.clientX, mev.clientY);
        const delta = hit ? differenceInCalendarDays(hit.date, origin) : 0;
        setDrag(null);
        if (delta === 0) return;
        const patch = moveByDays(event, delta);
        if (patch) onMoveEvent?.(event, patch);
      },
      onCancel: () => setDrag(null)
    });
  }

  const dragCursor = drag ? 'cursor-grabbing' : '';

  function chipLabel(e: CalendarEvent) {
    const title = e.summary?.trim() || '(no title)';
    if (!e.start_time) return title;
    const t = format(new Date(e.start_time), 'h:mm a').toLowerCase().replace(' ', '');
    return `${t} ${title}`;
  }

  function barLabel(e: CalendarEvent) {
    const title = e.summary?.trim() || '(no title)';
    if (e.all_day || !e.start_time) return title;
    const t = format(new Date(e.start_time), 'h:mma').toLowerCase();
    return `${t} ${title}`;
  }

  const MORE_CARD_WIDTH = 280;
  const morePos = useMemo(() => {
    if (!moreHover) return null;
    const estHeight = moreEvents.length * 22 + 40;
    const left = Math.max(
      8,
      Math.min(moreHover.rect.left, window.innerWidth - MORE_CARD_WIDTH - 8)
    );
    const spaceBelow = window.innerHeight - moreHover.rect.bottom - 8;
    const up = spaceBelow < estHeight && moreHover.rect.top > spaceBelow;
    return { left, top: up ? moreHover.rect.top - 4 : moreHover.rect.bottom + 4, up };
  }, [moreHover, moreEvents.length]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid grid-cols-7 border-b border-[var(--border)]">
        {dowLabels.map((d) => (
          <div key={d} className="px-3 py-2 text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
            {d}
          </div>
        ))}
      </div>
      <div
        ref={gridRef}
        className={clsx('grid min-h-0 flex-1', dragCursor)}
        style={{ gridTemplateRows: `repeat(${weeks.length}, 1fr)` }}
      >
        {weeks.map((week, wi) => {
          const allSegments = segmentsByWeek[wi];
          const visible = allSegments.filter((s) => s.row < maxBarRows);
          const hiddenPerCol = new Array(7).fill(0) as number[];
          for (const s of allSegments) {
            if (s.row >= maxBarRows) {
              for (let c = s.startCol; c <= s.endCol; c++) hiddenPerCol[c]++;
            }
          }
          // Per-column bar-row count: a cell only reserves vertical space for
          // bars that actually pass through its column, not for the busiest
          // column in the week.
          const barRowsPerCol = new Array(7).fill(0) as number[];
          for (const s of visible) {
            for (let c = s.startCol; c <= s.endCol; c++) {
              barRowsPerCol[c] = Math.max(barRowsPerCol[c], s.row + 1);
            }
          }

          return (
            <div key={week[0].toISOString()} className="relative grid min-h-0 grid-cols-7">
              {week.map((day, ci) => {
                const dayKey = format(day, 'yyyy-MM-dd');
                const dayChips = chipsByDay.get(dayKey) ?? [];
                const other = !isSameMonth(day, anchor);
                const isToday = isSameDay(day, today);
                const hasNote = noteDates?.has(dayKey) ?? false;
                const showNotePin = !!onOpenDailyNote && (hasNote || dayKey === todayKey);
                const dayWeather =
                  onOpenWeather && weather?.prefs.overlays.includes('month')
                    ? weather.data.days[dayKey]
                    : undefined;
                const showFooter = showNotePin || !!dayWeather;
                const chipPad =
                  barRowsPerCol[ci] > 0
                    ? barRowsPerCol[ci] * (BAR_HEIGHT + BAR_GAP) + TOP_PAD - 2
                    : 0;
                // Available chip area = row height minus header and bar pad,
                // with a small bottom margin. Always show at least one chip if
                // there are any, so the "+N more" message isn't the only thing.
                const chipArea =
                  rowHeight - CELL_HEADER_HEIGHT - chipPad - 4 - (showFooter ? NOTE_FOOTER_HEIGHT : 0);
                const maxChipsForCell = Math.max(1, Math.floor(chipArea / CHIP_ROW_HEIGHT));
                const fits = dayChips.length <= maxChipsForCell;
                const shown = fits
                  ? dayChips
                  : dayChips.slice(0, Math.max(0, maxChipsForCell - 1));
                const chipOverflow = dayChips.length - shown.length;
                const totalOverflow = chipOverflow + hiddenPerCol[ci];
                const isDropTarget = drag?.targetDayKey === dayKey && drag.daysDelta !== 0;
                return (
                  <div
                    key={day.toISOString()}
                    ref={(el) => {
                      if (el) cellRefs.current.set(dayKey, el);
                      else cellRefs.current.delete(dayKey);
                    }}
                    className={clsx(
                      'group relative flex min-h-[100px] flex-col items-stretch overflow-hidden border-b border-r border-[var(--border)] bg-[var(--bg)] px-1 py-1 text-left',
                      other && 'bg-[var(--bg-sunken)] text-[var(--text-faint)]',
                      isDropTarget && 'bg-[var(--bg-hover)] ring-2 ring-inset ring-[var(--accent)]'
                    )}
                  >
                    {dayPulse && isSameDay(day, dayPulse.day) && (
                      <div
                        key={`pulse-${dayPulse.key}`}
                        aria-hidden
                        className="today-pulse pointer-events-none absolute inset-0"
                        style={{ background: 'var(--today-fg)' }}
                      />
                    )}
                    <div className="flex items-center justify-between">
                      <span
                        className={clsx(
                          'rounded-full px-[6px] py-px text-[0.78rem] leading-[1.1]',
                          isToday
                            ? 'bg-[var(--accent)] font-semibold text-white'
                            : 'text-[var(--text-muted)]'
                        )}
                      >
                        {format(day, day.getDate() === 1 ? 'MMM d' : 'd')}
                      </span>
                      <button
                        type="button"
                        aria-label="new event"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                          onCreateAt?.(day, rect);
                        }}
                        className="ml-1 grid h-4 w-4 flex-shrink-0 place-items-center rounded text-[0.78rem] leading-none text-[var(--text-faint)] opacity-0 hover:bg-[var(--bg-hover)] hover:text-[var(--text)] group-hover:opacity-100"
                      >
                        +
                      </button>
                    </div>
                    <div
                      className="mt-[2px] flex flex-col gap-[2px] overflow-hidden"
                      style={{ paddingTop: chipPad }}
                    >
                      {shown.map((e) => {
                        const bg = retintEventBackground(e.background_color, theme);
                        const isDragging = drag?.event.id === e.id;
                        const movable = !!onMoveEvent && !!canMoveEvent?.(e);
                        return (
                          <button
                            key={e.id}
                            type="button"
                            title={e.summary ?? ''}
                            onMouseDown={(ev) => beginDrag(e, ev)}
                            onClick={(ev) => {
                              if (consumeDragClick()) {
                                ev.preventDefault();
                                ev.stopPropagation();
                                return;
                              }
                              ev.stopPropagation();
                              const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                              onPickEvent?.(e.id, rect);
                            }}
                            onContextMenu={(ev) => {
                              if (!onEventContextMenu) return;
                              ev.preventDefault();
                              ev.stopPropagation();
                              onEventContextMenu(e.id, ev.clientX, ev.clientY);
                            }}
                            className={clsx(
                              'flex items-center gap-1 overflow-hidden whitespace-nowrap rounded-[3px] px-[5px] py-[1px] text-left text-[0.72rem] leading-snug text-[var(--text)] hover:bg-[var(--bg-hover)]',
                              movable && 'cursor-grab',
                              isDragging && 'opacity-40'
                            )}
                          >
                            <span
                              className="h-[6px] w-[6px] flex-shrink-0 rounded-full"
                              style={{ background: bg }}
                            />
                            <span className="overflow-hidden text-ellipsis">{chipLabel(e)}</span>
                          </button>
                        );
                      })}
                      {totalOverflow > 0 && (
                        <div
                          className="cursor-default px-[5px] text-[0.7rem] text-[var(--text-faint)] hover:text-[var(--text-muted)]"
                          onMouseEnter={(ev) => openMore(dayKey, day, ev.currentTarget)}
                          onMouseLeave={scheduleMoreClose}
                        >
                          +{totalOverflow} more
                        </div>
                      )}
                    </div>
                    {showFooter && (
                      // Daily note bottom left, weather bottom right.
                      <div className="mt-auto flex min-w-0 items-center gap-1 pt-[2px]">
                        {showNotePin && onOpenDailyNote && (
                          <DailyNotePin
                            date={dayKey}
                            hasNote={hasNote}
                            isToday={dayKey === todayKey}
                            variant="cell"
                            onOpen={onOpenDailyNote}
                          />
                        )}
                        {dayWeather && weather && onOpenWeather && (
                          <div className="ml-auto flex min-w-0 justify-end">
                            <DayWeather
                              date={dayKey}
                              day={dayWeather}
                              prefs={weather.prefs}
                              onOpen={onOpenWeather}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {weather && (showTempGraph || showPrecipBars || showSkyRibbon) && (
                <WeekMeteogram
                  week={week}
                  hoursByDay={weather.hoursByDay}
                  scale={tempScale}
                  showTemp={showTempGraph}
                  showPrecip={showPrecipBars}
                  showSky={showSkyRibbon}
                  temperatureUnit={weather.prefs.temperature_unit}
                  precipitationUnit={weather.prefs.precipitation_unit}
                />
              )}
              {visible.map((s) => {
                const bg = retintEventBackground(s.event.background_color, theme);
                const fg = retintEventForeground(s.event.background_color, theme);
                const widthPct = ((s.endCol - s.startCol + 1) / 7) * 100;
                const leftPct = (s.startCol / 7) * 100;
                const top = HEADER_HEIGHT + s.row * (BAR_HEIGHT + BAR_GAP);
                const isDragging = drag?.event.id === s.event.id;
                const movable = !!onMoveEvent && !!canMoveEvent?.(s.event);
                return (
                  <button
                    type="button"
                    key={`${s.event.id}-${s.startCol}`}
                    title={s.event.summary ?? ''}
                    onMouseDown={(ev) => beginDrag(s.event, ev)}
                    onClick={(ev) => {
                      if (consumeDragClick()) {
                        ev.preventDefault();
                        ev.stopPropagation();
                        return;
                      }
                      ev.stopPropagation();
                      const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                      onPickEvent?.(s.event.id, rect);
                    }}
                    onContextMenu={(ev) => {
                      if (!onEventContextMenu) return;
                      ev.preventDefault();
                      ev.stopPropagation();
                      onEventContextMenu(s.event.id, ev.clientX, ev.clientY);
                    }}
                    className={clsx(
                      'absolute flex items-center overflow-hidden whitespace-nowrap px-[6px] text-left text-[0.72rem] font-medium leading-snug hover:brightness-95',
                      s.startsBefore ? 'rounded-l-none' : 'rounded-l-[3px]',
                      s.endsAfter ? 'rounded-r-none' : 'rounded-r-[3px]',
                      movable && 'cursor-grab',
                      isDragging && 'opacity-40'
                    )}
                    style={{
                      left: `calc(${leftPct}% + 4px)`,
                      width: `calc(${widthPct}% - 8px)`,
                      top: `${top}px`,
                      height: `${BAR_HEIGHT}px`,
                      background: bg,
                      color: fg
                    }}
                  >
                    {!s.startsBefore && (
                      <span className="overflow-hidden text-ellipsis">{barLabel(s.event)}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      {moreHover && morePos && (
        <div
          className="fixed z-40 flex flex-col gap-[2px] rounded-lg border border-[var(--border-strong)] bg-[var(--popover-bg)] p-2 shadow-2xl"
          style={{
            left: morePos.left,
            top: morePos.top,
            width: MORE_CARD_WIDTH,
            transform: morePos.up ? 'translateY(-100%)' : undefined
          }}
          onMouseEnter={cancelMoreClose}
          onMouseLeave={scheduleMoreClose}
        >
          <div className="px-[5px] pb-1 text-[0.7rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">
            {format(moreHover.day, 'EEE, MMM d')}
          </div>
          {moreEvents.map((e) => {
            const bg = retintEventBackground(e.background_color, theme);
            return (
              <button
                key={e.id}
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                  onPickEvent?.(e.id, rect);
                }}
                className="flex items-center gap-1 overflow-hidden whitespace-nowrap rounded-[3px] px-[5px] py-[2px] text-left text-[0.72rem] leading-snug text-[var(--text)] hover:bg-[var(--bg-hover)]"
              >
                <span
                  className="h-[6px] w-[6px] flex-shrink-0 rounded-full"
                  style={{ background: bg }}
                />
                <span className="overflow-hidden text-ellipsis">{chipLabel(e)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
